if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.showRequest = 0;

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      clearShowLoading(opener) {
        if (!opener) return;
        opener.removeAttribute('aria-disabled');
        opener.classList.remove('loading');
        opener.querySelector('.loading__spinner')?.classList.add('hidden');
      }

      cancelShow() {
        ++this.showRequest;
        this.showController?.abort();
        this.showController = null;
        this.clearShowLoading(this.showOpener);
        this.showOpener = null;
      }

      disconnectedCallback() {
        // Cards can be refreshed while their product-options request is pending.
        this.cancelShow();
      }

      hide(preventFocus = false) {
        this.cancelShow();
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        this.cancelShow();
        if (!this.isConnected || !opener?.isConnected) return Promise.resolve(false);
        this.showErrorNode?.remove();
        const request = ++this.showRequest;
        const controller = new AbortController();
        this.showController = controller;
        this.showOpener = opener;
        const current = () => request === this.showRequest && this.isConnected && opener.isConnected;
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner')?.classList.remove('hidden');

        let deadline;
        const stopped = new Promise((_, reject) => {
          controller.signal.addEventListener('abort', () => reject(new DOMException('Request aborted', 'AbortError')), { once: true });
          deadline = setTimeout(() => {
            reject(new Error('Product options took too long to load'));
            controller.abort();
          }, 10000);
        });
        const loading = (async () => {
          const response = await fetch(opener.getAttribute('data-product-url'), { signal: controller.signal });
          if (!response.ok) throw new Error('Product options could not be loaded');
          return await response.text();
        })();
        return Promise.race([loading, stopped])
          .then((responseText) => {
            if (!current()) return false;
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');
            if (!productElement) throw new Error('Product options are unavailable');

            this.preprocessHTML(productElement, opener);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            if (!current()) return false;

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);
            return true;
          })
          .catch(() => {
            if (!current()) return false;
            const message = document.createElement('div');
            message.className = 'wi-quick-add-error';
            message.setAttribute('role', 'alert');
            message.append(document.createTextNode('Could not load these options. '));
            const link = document.createElement('a');
            const fallback = this.getNavigationURL(opener.getAttribute('data-product-url'), opener.getAttribute('data-navigation-url'));
            link.href = `${fallback.pathname}${fallback.search}${fallback.hash}`;
            link.textContent = 'View product to choose your options';
            message.append(link);
            (opener.closest('modal-opener') || opener).insertAdjacentElement('afterend', message);
            this.showErrorNode = message;
            return false;
          })
          .finally(() => {
            clearTimeout(deadline);
            if (request !== this.showRequest) return;
            this.clearShowLoading(opener);
            this.showController = null;
            this.showOpener = null;
          });
      }

      preprocessHTML(productElement, opener) {
        if (productElement.tagName === 'PRODUCT-INFO') this.prepareNavigation(productElement, opener);
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      getNavigationURL(url, candidate) {
        const productUrl = new URL(url, window.shopUrl || location.origin);
        const navigation = new URL(productUrl.href);
        if (candidate) {
          try {
            const context = new URL(candidate, productUrl.href);
            const nativePath = productUrl.pathname.match(/^(.*)\/products\/([^/]+)\/?$/);
            const contextPath = context.pathname.match(/^(.*)\/collections\/([^/]+)\/products\/([^/]+)\/?$/);
            const sameCollectionProduct = nativePath && contextPath && contextPath[1] === nativePath[1] &&
              contextPath[2] !== 'all' && contextPath[3] === nativePath[2];
            if (context.origin === productUrl.origin && (context.pathname === productUrl.pathname || sameCollectionProduct)) {
              navigation.pathname = context.pathname;
              for (const [key, value] of context.searchParams) {
                if (!['variant', 'section_id', 'option_values'].includes(key) && !navigation.searchParams.has(key)) {
                  navigation.searchParams.append(key, value);
                }
              }
            }
          } catch {
            // Invalid or unrelated navigation context keeps the genuine product route.
          }
        }
        return navigation;
      }

      prepareNavigation(productElement, opener) {
        const productUrl = new URL(productElement.dataset.url, window.shopUrl || location.origin);
        let candidate = productElement.dataset.navigationUrl;
        if (opener) {
          const request = new URL(opener.getAttribute('data-product-url'), productUrl.href);
          candidate = request.origin === productUrl.origin && request.pathname === productUrl.pathname ?
            opener.getAttribute('data-navigation-url') : '';
        }
        const navigation = this.getNavigationURL(productUrl.href, candidate);
        productElement.dataset.navigationUrl = `${navigation.pathname}${navigation.search}${navigation.hash}`;
        const variantId = productElement.querySelector('product-form input[name="id"]')?.getAttribute('value');
        if (variantId && /^\d+$/.test(variantId)) navigation.searchParams.set('variant', variantId);
        productElement.querySelectorAll('a.product__title, a.product__view-details').forEach((link) => {
          link.href = `${navigation.pathname}${navigation.search}${navigation.hash}`;
        });
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
